import { Raffle } from "@/app/entities/Raffle";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useRaffles } from "@/app/hooks/useRaffles";
import { PaginatedTable } from "@/view/components/PaginatedTable";
import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { PlusCircle, Search } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import useRaffleAdminTable from "./hooks/useRaffleAdminTable";
import { useAuth } from "@/app/hooks/useAuth";

export const ManageRafflesPage = () => {
  const [search, setSearchValue] = useState("");
  const [skip, setSkip] = useState(0);
  const debouncedSearchValue = useDebounce(500, search);

  const { data } = useRaffles({
    search: debouncedSearchValue ?? "",
    skip,
    limit: 10,
  });

  const table = useRaffleAdminTable({ data: data?.prizeDraws || [] });

  const navigate = useNavigate();

  const { PERMISSIONS } = useAuth();

  const handleChangeSkip = useCallback((skip: number) => {
    setSkip(skip);
  }, []);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    },
    [],
  );

  const handleCreateRaffle = () => {
    navigate("/sorteios/gerenciar/new");
  };

  const handleViewRaffle = (raffle: Raffle) => {
    navigate(`/sorteios/gerenciar/view/${raffle?._id}`);
  };

  return (
    <div className="flex w-full flex-col px-2 sm:px-4 lg:px-0">
      <h1 className="mb-4 text-xl font-semibold sm:mb-5 sm:text-2xl lg:text-3xl">Sorteios</h1>
      <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="relative flex h-full items-center">
          <Search
            size={18}
            className="absolute left-3 top-[50%] z-10 -translate-y-[50%] text-gray-400 sm:left-4 sm:size-5"
          />
          <Input
            className="h-[42px] w-full rounded-full pl-10 pr-4 text-sm transition-all focus:ring-2 focus:ring-primary/20 sm:h-[44px] sm:w-64 sm:pl-12 md:w-80 lg:w-96"
            placeholder="Pesquisar sorteio..."
            onChange={handleSearchInputChange}
            value={search}
          />
        </div>
        {PERMISSIONS.raffles.create && (
          <Button
            className="flex h-[42px] w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-white transition-all hover:bg-primary/90 focus:ring-2 focus:ring-primary/20 sm:h-[44px] sm:w-auto sm:px-6"
            onClick={handleCreateRaffle}
          >
            <PlusCircle className="size-4 sm:size-5" />
            <span>Novo Sorteio</span>
          </Button>
        )}
      </div>
      <div className="w-full overflow-hidden">
        <PaginatedTable<Raffle>
          table={table}
          pagination={data}
          onChangeSkip={handleChangeSkip}
          handleRowClick={handleViewRaffle}
          type="table with lines"
        />
      </div>
    </div>
  );
};
